import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { matchPath, useLocation } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2, Upload } from 'lucide-react';
import {
  AssistantMessageStatus,
  type AssistantDecision,
  type AssistantPageContext,
  type AssistantUpload,
} from '@xtreed/academy-shared';
import { cn } from '@/lib/utils';
import { notifyApiError } from '@/lib/api-message';
import { startUpload } from '@/lib/assistant-uploader';
import { useAssistantStore } from '@/stores/assistant';
import { assistantKeys } from '@/hooks/assistant/assistantKeys';
import {
  useAssistantConversations,
  useCreateAssistantConversation,
  useDeleteAssistantConversation,
} from '@/hooks/assistant/useAssistantConversations';
import { useAssistantMessages } from '@/hooks/assistant/useAssistantMessages';
import { useAssistantRun } from '@/hooks/assistant/useAssistantRun';
import {
  useAssistantUploads,
  type AssistantUploadView,
} from '@/hooks/assistant/useAssistantUploads';
import { AssistantHeader } from './AssistantHeader';
import { AssistantEmptyState } from './AssistantEmptyState';
import { Composer } from './Composer';
import { MessageList } from './MessageList';
import { UploadTray } from './UploadTray';

function usePageContext(): AssistantPageContext {
  const { pathname } = useLocation();
  return useMemo(() => {
    const course = matchPath('/admin/courses/:id/*', pathname);
    const module = matchPath('/admin/modules/:id/*', pathname);
    return {
      route: pathname,
      productId: course?.params.id ?? null,
      moduleId: module?.params.id ?? null,
    };
  }, [pathname]);
}

export function AssistantPanel() {
  const { t } = useTranslation('adminAssistant');
  const queryClient = useQueryClient();
  const {
    isOpen,
    setOpen,
    activeConversationId,
    setActiveConversation,
    localUploads,
  } = useAssistantStore();
  const context = usePageContext();
  const conversations = useAssistantConversations(isOpen);
  const createConversation = useCreateAssistantConversation();
  const deleteConversation = useDeleteAssistantConversation();
  const history = useAssistantMessages(activeConversationId);
  const { uploads } = useAssistantUploads(activeConversationId);
  const run = useAssistantRun();
  const [draft, setDraft] = useState('');
  const [pending, setPending] = useState<AssistantUpload[]>([]);

  useEffect(() => {
    if (
      activeConversationId &&
      conversations.isSuccess &&
      !conversations.isFetching &&
      !conversations.data.some((item) => item.id === activeConversationId)
    ) {
      setActiveConversation(null);
    }
  }, [
    activeConversationId,
    conversations.data,
    conversations.isFetching,
    conversations.isSuccess,
    setActiveConversation,
  ]);

  useEffect(() => {
    setPending([]);
  }, [activeConversationId]);

  const uploadsById = useMemo(() => {
    const map = new Map<string, AssistantUploadView>();
    uploads.forEach((upload) => map.set(upload.id, upload));
    return map;
  }, [uploads]);

  const pendingViews: AssistantUploadView[] = pending.map(
    (upload) =>
      uploadsById.get(upload.id) ?? {
        ...upload,
        local: localUploads[upload.id] ?? null,
      },
  );

  const ensureConversation = useCallback(async () => {
    if (activeConversationId) return activeConversationId;
    const conversation = await createConversation.mutateAsync(undefined);
    return conversation.id;
  }, [activeConversationId, createConversation]);

  const attach = useCallback(
    async (files: File[]) => {
      let conversationId: string;
      try {
        conversationId = await ensureConversation();
      } catch {
        return;
      }
      for (const file of files) {
        try {
          const upload = await startUpload(conversationId, file);
          setPending((current) => [...current, upload]);
        } catch (error) {
          notifyApiError(error, t('errors.upload', { name: file.name }));
        }
      }
      queryClient.invalidateQueries({
        queryKey: assistantKeys.uploads(conversationId),
      });
    },
    [ensureConversation, queryClient, t],
  );

  const send = useCallback(
    async (message: string) => {
      let conversationId: string;
      try {
        conversationId = await ensureConversation();
      } catch {
        return;
      }
      const attachments = pending;
      setDraft('');
      setPending([]);
      const ok = await run.send({
        conversationId,
        message,
        attachments,
        context,
      });
      if (!ok) {
        setDraft(message);
        setPending(attachments);
      }
    },
    [context, ensureConversation, pending, run],
  );

  const decide = useCallback(
    (interruptId: string, decisions: AssistantDecision[]) => {
      if (!activeConversationId || run.isRunning) return;
      void run.resume(activeConversationId, interruptId, decisions);
    },
    [activeConversationId, run],
  );

  const { getRootProps, isDragActive } = useDropzone({
    noClick: true,
    noKeyboard: true,
    onDrop: (files) => {
      if (files.length) void attach(files);
    },
  });

  const messages = history.messages;
  const last = messages[messages.length - 1];
  const lastPart = last?.parts[last.parts.length - 1];
  const isTyping =
    run.isRunning &&
    (last?.status !== AssistantMessageStatus.STREAMING ||
      !lastPart ||
      lastPart.type !== 'text');
  const busy = createConversation.isPending;
  const showEmpty =
    !history.isLoading && messages.length === 0 && !run.isRunning;

  return (
    <aside
      aria-label={t('panel.title')}
      aria-hidden={!isOpen}
      inert={!isOpen}
      className={cn(
        'flex-col overflow-hidden bg-card/95 backdrop-blur-xl',
        isOpen ? 'fixed inset-0 z-50 flex' : 'hidden',
        'lg:sticky lg:top-0 lg:z-40 lg:flex lg:h-screen lg:shrink-0 lg:border-border/50 lg:transition-[width] lg:duration-300 lg:ease-smooth',
        isOpen ? 'lg:w-[420px] lg:border-l' : 'lg:w-0',
      )}
    >
      <div
        {...getRootProps({
          className: 'relative flex h-full w-full flex-col lg:w-[420px]',
        })}
      >
        <AssistantHeader
          conversations={conversations.data ?? []}
          activeConversationId={activeConversationId}
          onSelect={setActiveConversation}
          onNew={() => {
            setActiveConversation(null);
            setDraft('');
          }}
          onDelete={(id) => deleteConversation.mutate(id)}
          onClose={() => setOpen(false)}
        />

        {history.isLoading && activeConversationId ? (
          <div className="flex flex-1 items-center justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : showEmpty ? (
          <AssistantEmptyState
            disabled={busy}
            onCoursePage={Boolean(context.productId)}
            onPick={(prompt) => void send(prompt)}
          />
        ) : (
          <MessageList
            key={activeConversationId ?? 'new'}
            messages={messages}
            uploadsById={uploadsById}
            isRunning={run.isRunning}
            isTyping={isTyping}
            hasOlder={Boolean(history.hasNextPage)}
            isLoadingOlder={history.isFetchingNextPage}
            onLoadOlder={() => void history.fetchNextPage()}
            onDecide={decide}
          />
        )}

        <UploadTray uploads={uploads} />

        <Composer
          isOpen={isOpen}
          isRunning={run.isRunning}
          isDisabled={busy}
          attachments={pendingViews}
          draft={draft}
          onDraftChange={setDraft}
          onSend={(message) => void send(message)}
          onStop={run.stop}
          onAttach={(files) => void attach(files)}
          onRemoveAttachment={(id) =>
            setPending((current) =>
              current.filter((upload) => upload.id !== id),
            )
          }
        />

        {isDragActive && (
          <div className="pointer-events-none absolute inset-2 z-10 flex flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-primary/60 bg-background/80 text-sm font-medium text-primary-ink backdrop-blur-xs">
            <Upload className="h-6 w-6" />
            {t('composer.dropHere')}
          </div>
        )}
      </div>
    </aside>
  );
}
